import Cookies from "js-cookie";
import appConfig from "@/config/default";

export const ACCESS_TOKEN_COOKIE = "userAccessToken";
export const REFRESH_TOKEN_COOKIE = "userRefreshToken";
export const SESSION_TYPE_COOKIE = "authSessionType";
export const AUTH_SYNC_STORAGE_KEY = "manabakh:auth-sync";

const isProduction = process.env.NODE_ENV === "production";

function getCookieDomain() {
  const configuredDomain = appConfig.DEPLOY_URL?.trim();
  if (!configuredDomain) return undefined;

  const normalizedDomain = configuredDomain
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .split(":")[0];

  if (!normalizedDomain || normalizedDomain === "localhost") {
    return undefined;
  }

  return normalizedDomain;
}

function getCookieOptions() {
  const domain = getCookieDomain();

  return {
    expires: 365,
    path: "/",
    sameSite: "lax",
    secure: isProduction,
    ...(domain ? { domain } : {}),
  };
}

function publishAuthChange(type) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      AUTH_SYNC_STORAGE_KEY,
      JSON.stringify({ type, timestamp: Date.now() })
    );
  } catch {
    // Cross-tab synchronization is best effort only.
  }
}

export function getAuthTokens() {
  return {
    accessToken: Cookies.get(ACCESS_TOKEN_COOKIE) || null,
    refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE) || null,
    sessionType: Cookies.get(SESSION_TYPE_COOKIE) || "client",
  };
}

/**
 * Persist a rotated token pair atomically from the application's perspective.
 * When older backend versions omit a new refresh token, retain the current one.
 */
export function setAuthTokens(
  accessToken,
  refreshToken,
  { sessionType = "client", sync = true } = {}
) {
  if (!accessToken) {
    throw new Error("An access token is required");
  }

  const options = getCookieOptions();
  Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, options);

  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_COOKIE, refreshToken, options);
  }
  Cookies.set(SESSION_TYPE_COOKIE, sessionType, options);

  if (sync) publishAuthChange("tokens-updated");
}

export function clearAuthTokens({ sync = true } = {}) {
  const options = getCookieOptions();

  Cookies.remove(ACCESS_TOKEN_COOKIE, options);
  Cookies.remove(REFRESH_TOKEN_COOKIE, options);
  Cookies.remove(SESSION_TYPE_COOKIE, options);
  Cookies.remove("persist:auth", options);

  // Also remove host-only and legacy values created by older client versions.
  Cookies.remove(ACCESS_TOKEN_COOKIE);
  Cookies.remove(REFRESH_TOKEN_COOKIE);
  Cookies.remove(SESSION_TYPE_COOKIE);
  Cookies.remove("persist:auth");

  if (typeof window !== "undefined") {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
    window.localStorage.removeItem("activeProfile");
  }

  if (sync) publishAuthChange("logout");
}
